import type { ConciergeCopy } from '../model/checkout_copy.js';
import type { ConciergeProductType, ConciergeRequestSource } from '../model/checkout_source.js';

type RequestOf<P extends ConciergeProductType> = Extract<
  ConciergeRequestSource,
  { productType: P }
>;

/**
 * O que cada produto decide no checkout de PEDIDO à equipe VOLL (modo
 * `request`). O editor do pedido é o `ConciergeRequestCard`, o mesmo para todos
 * os produtos.
 *
 * Métodos, não propriedades-função, de propósito: o parâmetro bivariante deixa
 * o módulo de um produto caber no tipo do registro inteiro.
 */
interface RequestProductModule<P extends ConciergeProductType> {
  /* O manifesto do form de viajantes é a regra do contrato
   * (`CheckoutRules.conciergeFields`): voo, hotel e carro não pedem identidade;
   * o ônibus pede a da reserva. */
  /** Hotel e carro: a alocação é a do solicitante (no carro ele é o único
   *  viajante, o motorista); voo e ônibus alocam por bloco. */
  readonly allocationOwner: 'every-traveler' | 'requester';
  /** Início da viagem (YYYY-MM-DD) para a consulta de saldo. */
  startDate(request: RequestOf<P>): string | undefined;
  /** Trechos do pedido — um período escolhido por trecho. */
  segmentCount(request: RequestOf<P>): number;
  /** O chip de contexto do topo da gaveta. */
  chip(request: RequestOf<P>, copy: ConciergeCopy): string;
}

const REQUEST_PRODUCTS: {
  readonly [P in ConciergeProductType]: RequestProductModule<P>;
} = {
  flight: {
    allocationOwner: 'every-traveler',
    startDate: (request) => request.segments[0]?.date,
    segmentCount: (request) => request.segments.length,
    chip: (request, copy) =>
      [
        copy.chip.flight,
        request.segments
          .map((segment) => `${segment.origin.iata} → ${segment.destination.iata}`)
          .join(' · '),
      ].join(' · '),
  },
  hotel: {
    allocationOwner: 'requester',
    startDate: (request) => request.checkIn,
    segmentCount: () => 0,
    chip: (request, copy) => [copy.chip.hotel, request.city ?? request.place].join(' · '),
  },
  bus: {
    // Como a reserva de ônibus: cada viajante leva o próprio rateio.
    allocationOwner: 'every-traveler',
    startDate: (request) => request.outwardDate,
    // Uma perna na ida, duas na ida-e-volta — um período por perna.
    segmentCount: (request) => (request.returnDate ? 2 : 1),
    chip: (request, copy) =>
      [copy.chip.bus, `${request.from.name} → ${request.to.name}`].join(' · '),
  },
  rental: {
    allocationOwner: 'requester',
    startDate: (request) => request.pickupAt.slice(0, 10),
    segmentCount: () => 0,
    chip: (request, copy) => [copy.chip.rental, request.pickup.label].join(' · '),
  },
};

/** O módulo do produto do pedido (cast: microsoft/TypeScript#30581, como em
 *  `productModule`). */
export function requestModule<R extends ConciergeRequestSource>(
  request: R,
): RequestProductModule<R['productType']> {
  return REQUEST_PRODUCTS[request.productType] as RequestProductModule<R['productType']>;
}
